import { Link, useParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { formatDate, getNewsById } from '@/utils/content';
import { news } from '@/mocks/news';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getNewsById(id) : undefined;

  if (!article) {
    return (
      <PageLayout>
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 py-28 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-4xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">该资讯不存在或已下线</h1>
          <p className="mt-3 text-sm text-foreground-500">你查看的资讯可能已被删除，去资讯专题页看看最新内容吧。</p>
          <Link
            to="/news"
            className="mt-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回资讯专题
          </Link>
        </div>
      </PageLayout>
    );
  }

  const related = news.filter((item) => item.id !== article.id).slice(0, 3);

  return (
    <PageLayout>
      <article className="mx-auto w-full max-w-3xl px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb
          items={[{ label: '首页', to: '/' }, { label: '资讯专题', to: '/news' }, { label: '正文' }]}
        />

        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
              {article.category}
            </span>
            <span className="text-xs text-foreground-500">{formatDate(article.date)}</span>
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-5 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {article.views.toLocaleString()} 次阅读
            </span>
          </div>
        </div>

        <div className="mt-6 h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 红杏影视资讯`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5 text-base leading-loose text-foreground-800">
          {article.content.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {article.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
              #{tag}
            </span>
          ))}
        </div>

        <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
            <i className="ri-information-line mt-0.5 text-base" />
            本文为红杏影视资讯内容，仅供影视资讯展示与交流，文中观点仅供参考，相关内容版权归原权利人所有。
          </p>
        </div>

        <section className="mt-10">
          <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">相关文章推荐</h2>
          <div className="flex flex-col gap-3">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200"
              >
                <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img src={item.cover} alt={item.title} className="h-full w-full object-cover object-top" />
                </div>
                <div className="min-w-0">
                  <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500">
                    {item.category} · {formatDate(item.date)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-10 flex justify-center">
          <Link
            to="/news"
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回资讯专题
          </Link>
        </div>
      </article>
    </PageLayout>
  );
}